import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * Page-scoped, non-fetch UI state for article listing/detail pages.
 *
 * This intentionally stays tiny: fetch/cache concerns live in
 * `store/api/articlesApi.ts` (RTK Query). Only the archived-article consent
 * popup - which has no data-fetching involved - lives here, per the
 * article-listing-ssr-rtk-query-rebuild design doc.
 */

export interface ArticleUiState {
  isArticleArchived: boolean;
  archivedArticleUrl: string;
  archivedSessionStorage: string;
}

export const articleUiInitialState: ArticleUiState = {
  isArticleArchived: false,
  archivedArticleUrl: '',
  archivedSessionStorage: 'archivedConsent',
};

const articleUiSlice = createSlice({
  name: 'articleUi',
  initialState: articleUiInitialState,
  reducers: {
    toggleArchivedModal: (state) => {
      state.isArticleArchived = !state.isArticleArchived;
    },
    closeArchivedModal: (state) => {
      state.isArticleArchived = false;
    },
    setArchivedArticleUrl: (state, action: PayloadAction<string>) => {
      state.archivedArticleUrl = action.payload;
    },
    setArchivedSessionStorageName: (state, action: PayloadAction<string>) => {
      if (action.payload) {
        state.archivedSessionStorage = action.payload;
      }
    },
    resetArticleUi: () => articleUiInitialState,
  },
});

export const {
  toggleArchivedModal,
  closeArchivedModal,
  setArchivedArticleUrl,
  setArchivedSessionStorageName,
  resetArticleUi,
} = articleUiSlice.actions;

interface ArticleUiRootState {
  articleUi: ArticleUiState;
}

export const selectIsArticleArchived = (state: ArticleUiRootState) =>
  state.articleUi.isArticleArchived;
export const selectArchivedArticleUrl = (state: ArticleUiRootState) =>
  state.articleUi.archivedArticleUrl;
export const selectArchivedSessionStorageName = (state: ArticleUiRootState) =>
  state.articleUi.archivedSessionStorage;

export default articleUiSlice.reducer;
